import {
  DeleteTwoTone,
  EditTwoTone,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Button,
  Select,
  Table,
  Tooltip,
  Form,
  notification,
  PageHeader,
  Switch,
  Row,
  Col,
} from "antd";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import IconNotFound from "../../static/icon/IconNotFound";
import IconLand from "../../static/icon/IconLand";
import DeleteAction from "../../Tools/DeleteAction";

import { useAuth } from "../../context/auth";
import MerchantDropdown from "../../components/Dropdown/MerchantDropdown";
import LandlordDropdown from "../../components/Dropdown/LandlordDropdown";
import { formatLandlordBusinessHoursType, listLandlordBusinessHoursType } from "../../utils/formatter";

const LandlordTable = () => {
  const history = useHistory();
  const { adminRole, accessToken } = useAuth();

  const [form] = Form.useForm();
  const selectedMerchants = Form.useWatch('merchantIds', form);

  const [loading, setLoading] = useState(false);

  const [update, setUpdate] = useState(0);
  const [total, setTotal] = useState(0);
  const [landlords, setLandlords] = useState([]);
  const [search, setSearch] = useState({
    pageValue: history.location.pageValue ?? {},
    searchValue: history.location.searchValue ?? {},
  });

  useEffect(() => {
    form.setFieldsValue(search.searchValue);
  }, [form, search]);

  const [currentLandlord, setCurrentLandlord] = useState(null);

  useEffect(() => {
    async function getMerchants({ pageValue, searchValue }) {
      setLoading(true);

      const url = new URL('/api/landlord', window.location);
      if (searchValue.merchantIds) for (const merchantId of searchValue.merchantIds) url.searchParams.append('merchantId', merchantId);
      if (searchValue.landlordIds) for (const landlordId of searchValue.landlordIds) url.searchParams.append('landlordId', landlordId);
      if (searchValue.businessHoursType) url.searchParams.append('businessHoursType', searchValue.businessHoursType);
      if (typeof searchValue.internal === 'boolean') url.searchParams.append('internal', searchValue.internal);
      if (typeof searchValue.active === 'boolean') url.searchParams.append('active', searchValue.active);

      if (pageValue.page) url.searchParams.append('page', pageValue.page);
      if (pageValue.size) url.searchParams.append('size', pageValue.size);
      if (pageValue.sort) url.searchParams.append('sort', pageValue.sort);
      if (pageValue.order) url.searchParams.append('order', pageValue.order);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { total, landlords } = await res.json();
        setTotal(total);
        setLandlords(landlords);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

      setLoading(false);
    };

    getMerchants(search);
  }, [search, accessToken, update]);

  const columns = [
    {
      title: "Cluster ID",
      dataIndex: "merchantId",
      key: "merchantId",
      hidden: !['admin', 'callcenter'].includes(adminRole),
      sorter: () => null,
    },
    {
      title: "Cluster Name",
      dataIndex: "merchantNameEn",
      key: "merchantNameEn",
      hidden: !['admin', 'callcenter'].includes(adminRole),
      sorter: () => null,
    },
    {
      title: "Site ID",
      dataIndex: "id",
      key: "id",
      sorter: () => null,
    },
    {
      title: "Site Name",
      dataIndex: "nameEn",
      key: "nameEn",
      sorter: () => null,
    },
    {
      title: "Address",
      dataIndex: "addrEn",
      key: "addrEn",
      sorter: () => null,
    },
    {
      title: "Business hour",
      dataIndex: "businessHoursType",
      key: "businessHoursType",
      width: 150,
      sorter: () => null,
      render: (businessHoursType) => formatLandlordBusinessHoursType(businessHoursType),
    },
    {
      title: "Area type",
      dataIndex: "internal",
      key: "internal",
      width: 120,
      sorter: () => null,
      render: (internal) => internal ? 'Internal Used' : 'Public Used',
    },
    {
      title: "Inactive/Active",
      dataIndex: "active",
      key: "active",
      width: "10%",
      align: "center",
      sorter: () => null,
      render: (active, landlord) => (
        <Switch
          checked={active}
          onChange={(active) => {
            updateLandlordActive(active, landlord);
            setUpdate(update + 1);
          }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, landlord) => (
        <span style={{ whiteSpace: "nowrap" }}>
          <Tooltip placement="bottom" title="View" className="mx-1">
            <EditTwoTone
              style={{
                backgroundColor: "#F3F6F9",
                padding: 6,
                borderRadius: 6,
              }}
              onClick={() => {
                history.push({
                  pathname: `/landlord/${landlord.id}`,
                  searchValue: search.searchValue,
                  pageValue: search.pageValue,
                });
              }}
              twoToneColor="#00C9D6"
            />
          </Tooltip>

          {!landlord.active && ['admin', 'merchant', 'landlord', 'callcenter'].includes(adminRole) && (
            <Tooltip placement="bottom" title="Delete" className="mx-1">
              <DeleteTwoTone
                style={{
                  backgroundColor: "#F3F6F9",
                  padding: 6,
                  borderRadius: 6,
                }}
                onClick={() => setCurrentLandlord(landlord)}
                twoToneColor="red"
              />
            </Tooltip>
          )}
        </span>
      ),
    },
  ].filter((item) => !item.hidden);

  async function updateLandlordActive(active, landlord) {
    const url = new URL('/api/landlord/' + landlord.id, window.location);

    const res = await fetch(url, {
      method: 'PATCH',
      headers: {
        'X-ACCESS-TOKEN': accessToken,
        'Content-type': 'application/json',
      },
      body: JSON.stringify({ active }),
    });
    if (res.ok) {
      notification.success({
        message: "Success",
        description: "Successfully saved data.",
      });
    } else {
      notification["error"]({
        message: "Something went wrong !",
        description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
      });
    }
  };

  const [downloading, setDownloading] = useState(false);
  async function downloadXlsx({ searchValue }) {
    setDownloading(true);

    const url = new URL('/api/landlord', window.location);
    url.searchParams.append('mode', 'xlsx');

    if (searchValue.merchantIds) for (const merchantId of searchValue.merchantIds) url.searchParams.append('merchantId', merchantId);
    if (searchValue.landlordIds) for (const landlordId of searchValue.landlordIds) url.searchParams.append('landlordId', landlordId);
    if (searchValue.businessHoursType) url.searchParams.append('businessHoursType', searchValue.businessHoursType);
    if (typeof searchValue.internal === 'boolean') url.searchParams.append('internal', searchValue.internal);
    if (typeof searchValue.active === 'boolean') url.searchParams.append('active', searchValue.active);

    const res = await fetch(url, {
      headers: { 'X-ACCESS-TOKEN': accessToken },
    });
    if (res.ok) {
      const data = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(data);
      a.download = `Landlord_${new Date().toISOString()}.xlsx`;
      a.click();
    } else {
      if (res.status === 404) {
        notification["error"]({
          message: "No matched landlord found",
        });
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }
    }

    setDownloading(false);
  }

  return (
    <>
      <div className="flex justify-between items-center mt-2">
        <PageHeader
          style={{ padding: 0, width: "100%" }}
          title={
            <span className="flex items-center mt-2">
              <IconLand /> &nbsp;&nbsp;Site
            </span>
          }
        />

        <Button
          className="mt-2 mr-3 flex items-center px-12 border-0"
          loading={downloading}
          type="primary"
          shape="round"
          style={{ background: "#1BC5BD" }}
          onClick={() => downloadXlsx(search)}
        >
          Export
        </Button>

        {['admin', 'merchant', 'landlord', 'callcenter'].includes(adminRole) && (
          <Button
            className="mt-2 flex items-center"
            icon={<PlusOutlined />}
            type="primary"
            shape="round"
            onClick={() =>
              history.push({
                pathname: `/landlord/new`,
                searchValue: search.searchValue,
                pageValue: search.pageValue,
              })
            }
          >
            New Site
          </Button>
        )}
      </div>
      <div className="content">
        <div className="">
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => setSearch(search => ({
              pageValue: { ...search.pageValue, page: 1 },
              searchValue: values,
            }))}
            onReset={() => form.submit()}
          >
            <Row align="middle" gutter={16}>
              {['admin', 'callcenter'].includes(adminRole) && (
                <Col className="gutter-row" span={8}>
                  <Form.Item
                    label="Cluster Name"
                    name="merchantIds"
                    colon={false}
                    initialValue={[]}
                  >
                    <MerchantDropdown
                      allowClear
                      allowSearch
                      mode="multiple"
                      setSelectedMerchants={(merchantIds) => form.setFieldsValue({ merchantIds })}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col className="gutter-row" span={8}>
                <Form.Item
                  label="Site Name"
                  name="landlordIds"
                  colon={false}
                  initialValue={[]}
                >
                  <LandlordDropdown
                    allowClear
                    allowSearch
                    mode="multiple"
                    merchantIds={selectedMerchants}
                    setSelectedLandlords={(landlordIds) => form.setFieldsValue({ landlordIds })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Business Hour"
                  name="businessHoursType"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Business Hour' },
                      ...listLandlordBusinessHoursType.map(businessHoursType => ({
                        value: businessHoursType.name,
                        label: businessHoursType.value,
                      })),
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Area Type"
                  name="internal"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Area Type' },
                      { value: true, label: 'Internal Used' },
                      { value: false, label: 'Public Used' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Status"
                  name="active"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Status' },
                      { value: true, label: 'Active' },
                      { value: false, label: 'Inactive' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={14}>
                <div className="flex">
                  <Button
                    className="w-28"
                    type="primary"
                    shape="round"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>

                  <Button
                    className="w-28 ml-2"
                    type="primary"
                    ghost
                    shape="round"
                    htmlType="reset"
                    icon={<SyncOutlined />}
                  >
                    Clear Data
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
      <div className="content">
        <Table
          className="inbox_table"
          locale={{ emptyText: <IconNotFound /> }}
          rowKey={(landlord) => landlord.id}
          loading={loading}
          columns={columns}
          dataSource={landlords}
          pagination={{
            size: "small",
            current: search.pageValue.page,
            pageSize: search.pageValue.size,
            defaultPageSize: 10,
            total,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total, range) => ` ${range[0]}-${range[1]} of ${total} `,
          }}
          onChange={(pagination, _, sorter) => setSearch(search => ({
            pageValue: {
              page: pagination.current ?? 1,
              size: pagination.pageSize ?? 10,
              sort: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'asc' : 'desc',
            },
            searchValue: search.searchValue,
          }))}
        />
      </div>

      <DeleteAction
        openModalActionDelete={!!currentLandlord}
        name="Site"
        type="landlord"
        editRecord={currentLandlord}
        onCancel={() => setCurrentLandlord(null)}
        onOk={() => {
          setCurrentLandlord(null);
          setUpdate(update + 1);
        }}
      />
    </>
  );
};

export default LandlordTable;
